import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ALL_ENTITLEMENTS, getMyEntitlements } from '../services/entitlementsService';
import type { FeatureKey, MyEntitlements } from '../services/entitlementsService';
import { onQuotaExceeded } from '../services/packageErrors';

/**
 * Funcionalidades del paquete de la cuenta del usuario (feature-106, AC-04).
 *
 * Una sola petición por usuario aunque la pidan muchos componentes a la vez:
 * la promesa se cachea en memoria y se descarta al cambiar de sesión. Un fallo
 * de red no oculta nada (todo permitido): ocultar por error sería peor, y el
 * backend devuelve 403 si de verdad no está en el paquete.
 */
let cache: { userId: string; promise: Promise<MyEntitlements>; at: number } | null = null;
const listeners = new Set<() => void>();

/**
 * `quotaExceeded` cambia sin que el usuario haga nada (otro usuario agota la cuota,
 * empieza el ciclo, el admin amplía el paquete): se vuelve a preguntar con la
 * ventana enfocada si el dato tiene más de un minuto, cada 5 min y tras un 403 de cuota (F7).
 */
export const ENTITLEMENTS_REFRESH_MS = 5 * 60_000;
const FOCUS_STALE_MS = 60_000;
let stopRefresh: (() => void) | null = null;

function startRefresh(): void {
  if (stopRefresh || typeof window === 'undefined') return;
  const onFocus = () => {
    if (cache && Date.now() - cache.at > FOCUS_STALE_MS) invalidateEntitlements();
  };
  const timer = window.setInterval(() => invalidateEntitlements(), ENTITLEMENTS_REFRESH_MS);
  const offQuota = onQuotaExceeded(() => invalidateEntitlements());
  window.addEventListener('focus', onFocus);
  stopRefresh = () => {
    window.clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    offQuota();
    stopRefresh = null;
  };
}

export interface EntitlementsState {
  /** `null` mientras carga. */
  features: ReadonlySet<FeatureKey> | null;
  quotaExceeded: boolean;
  has: (feature: FeatureKey) => boolean;
}

function toState(value: MyEntitlements | null): EntitlementsState {
  const features = value ? new Set(value.features) : null;
  return {
    features,
    quotaExceeded: value?.quotaExceeded ?? false,
    has: (feature) => Boolean(features?.has(feature)),
  };
}

export function loadEntitlements(userId: string): Promise<MyEntitlements> {
  if (!cache || cache.userId !== userId) {
    cache = { userId, promise: getMyEntitlements().catch((): MyEntitlements => ALL_ENTITLEMENTS), at: Date.now() };
  }
  return cache.promise;
}

export function useEntitlements(): EntitlementsState {
  const { user } = useAuth();
  const userId = user?.userId ?? null;
  const [loaded, setLoaded] = useState<{ userId: string; value: MyEntitlements } | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    startRefresh();
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) stopRefresh?.();
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      cache = null;
      return;
    }
    let cancelled = false;
    loadEntitlements(userId).then((value) => {
      if (!cancelled) setLoaded({ userId, value });
    });
    return () => { cancelled = true; };
  }, [userId, version]);

  // Lo cargado para otro usuario no vale: se trata como «cargando».
  return toState(loaded && loaded.userId === userId ? loaded.value : null);
}

/** Tras asignar o editar paquetes o un cambio de cuota: los componentes montados vuelven a preguntar. */
export function invalidateEntitlements(): void {
  cache = null;
  listeners.forEach((listener) => listener());
}
